import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import numeral from 'numeral';
import axiosInstance, { API_ENDPOINTS, HOST_API } from '../axios/axiosInstance';

// Mapa fixo de alergénios para números (baseado na tabela padrão EU)
const ALLERGEN_MAP = {
  'Glúten': 1, 'Gluten': 1,
  'Crustáceos': 2, 'Crustaceans': 2, 'Crustacés': 2,
  'Ovos': 3, 'Eggs': 3, 'Œufs': 3,
  'Peixe': 4, 'Fish': 4, 'Poisson': 4,
  'Amendoim': 5, 'Peanuts': 5, 'Arachides': 5,
  'Soja': 6, 'Soy': 6,
  'Leite': 7, 'Milk': 7, 'Lait': 7,
  'Frutos de Casca Rija': 8, 'Tree Nuts': 8, 'Fruits à Coque': 8,
  'Aipo': 9, 'Celery': 9, 'Céleri': 9,
  'Mostarda': 10, 'Mustard': 10, 'Moutarde': 10,
  'Sésamo': 11, 'Sesame': 11,
  'Dióxido de Enxofre e Sulfitos': 12, 'Sulphur Dioxide and Sulphites': 12, 'Dioxyde de Soufre et Sulfites': 12,
  'Tremoço': 13, 'Lupin': 13,
  'Moluscos': 14, 'Molluscs': 14, 'Mollusques': 14
};

const getAllergenNumber = (allergen) => {
  return ALLERGEN_MAP[allergen.portugueseName] ||
         ALLERGEN_MAP[allergen.englishName] ||
         ALLERGEN_MAP[allergen.frenchName] || null;
};

const DailyDish = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getDayName = (dayOfWeek) => {
    const days = {
      pt: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
      en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      fr: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
    };
    const lang = i18n.language || 'pt';
    return days[lang]?.[dayOfWeek] || days['pt'][dayOfWeek];
  };

  const getDishName = (dish) => {
    if (!dish) return '';
    const lang = i18n.language || 'pt';
    switch (lang) {
      case 'en': return dish.englishName || dish.portugueseName;
      case 'fr': return dish.frenchName || dish.portugueseName;
      default: return dish.portugueseName;
    }
  };

  const getDishDescription = (dish) => {
    if (!dish) return '';
    const lang = i18n.language || 'pt';
    switch (lang) {
      case 'en': return dish.englishDescription || dish.portugueseDescription || '';
      case 'fr': return dish.frenchDescription || dish.portugueseDescription || '';
      default: return dish.portugueseDescription || '';
    }
  };


  const getNote = () => {
    if (!data) return '';
    const lang = i18n.language || 'pt';
    switch (lang) {
      case 'en': return data.englishNote || data.portugueseNote;
      case 'fr': return data.frenchNote || data.portugueseNote;
      default: return data.portugueseNote;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : i18n.language === 'fr' ? 'fr-FR' : 'pt-PT', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getDateRangeLabel = () => {
    const lang = i18n.language || 'pt';
    const labels = {
      pt: { from: 'De', to: 'a' },
      en: { from: 'From', to: 'to' },
      fr: { from: 'Du', to: 'au' }
    };
    return labels[lang] || labels['pt'];
  };

  const fCurrency = (number) => {
    const format = number ? numeral(number).format('€0,0.00') : '';
    const isInteger = format.includes(',00');
    return isInteger ? format.replace(',00', '') : format;
  };

  useEffect(() => {
    const fetchDailyDishes = async () => {
      try {
        const res = await axiosInstance.get(HOST_API + API_ENDPOINTS.menu.dailyDish);
        setData(res.data);
      } catch (error) {
        console.error('Error fetching daily dishes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDailyDishes();
  }, []);

  if (loading || !data || !data.dishes || data.dishes.length === 0) {
    return null;
  }

  const dateLabels = getDateRangeLabel();
  const note = getNote();

  // Group dishes by day of week
  const dishesByDay = data.dishes.reduce((acc, dish) => {
    const day = dish.dayOfWeek;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(dish);
    return acc;
  }, {});

  // Sort days (1=Monday first, 0=Sunday last)
  const sortedDays = Object.keys(dishesByDay).sort((a, b) => {
    const dayA = parseInt(a) === 0 ? 7 : parseInt(a);
    const dayB = parseInt(b) === 0 ? 7 : parseInt(b);
    return dayA - dayB;
  });

  return (
    <section className="padding-b-50 text-center daily-dish-section">
      <div className="container">
        <div className="head_title text-center" style={{marginTop:'60px', marginBottom:'20px'}}>
          <h2>{t('home.dailyDish')}</h2>
          {data.startDate && data.endDate && (
            <p style={{
              fontSize: '14px',
              color: '#7e7b5e',
              marginTop: '5px',
              fontStyle: 'italic',
              letterSpacing: '1px'
            }}>
              {dateLabels.from} {formatDate(data.startDate)} {dateLabels.to} {formatDate(data.endDate)}
            </p>
          )}
        </div>

        <div className="menu-bar" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {sortedDays.map((day) => (
            <div key={day} style={{ marginBottom: '35px' }}>
              {/* Day Header */}
              <div style={{
                borderBottom: '1px solid #e0ded6',
                paddingBottom: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '400',
                  color: '#7e7b5e',
                  margin: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '2px'
                }}>
                  {getDayName(parseInt(day))}
                </h3>
              </div>

              {/* Dishes */}
              {dishesByDay[day].map((dish, dishIndex) => {
                const description = getDishDescription(dish);
                const allergens = dish.allergens || [];
                const allergenNumbers = allergens
                  .map(a => getAllergenNumber(a))
                  .filter(n => n !== null)
                  .sort((a, b) => a - b);

                return (
                  <div key={dishIndex} style={{ marginBottom: '18px', textAlign: 'left' }}>
                    {/* Dish name + dots + price */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '8px'
                    }}>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        {getDishName(dish)}
                       
                      </span>
                      <span style={{
                        flex: 1,
                        borderBottom: '1px dotted #ccc',
                        minWidth: '30px',
                        marginBottom: '4px'
                      }}></span>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333',
                        whiteSpace: 'nowrap'
                      }}>
                        {fCurrency(dish.cost)} €
                      </span>
                    </div>

                    {/* Description */}
                    {description && (
                      <p style={{
                        fontSize: '13px',
                        color: '#777',
                        margin: '4px 0 0 0',
                        fontStyle: 'italic',
                        lineHeight: '1.4'
                      }}>
                        {description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {note && (
            <div style={{
              marginTop: '30px',
              padding: '15px 20px',
              background: '#f9f8f4',
              borderLeft: '3px solid #7e7b5e',
              textAlign: 'left'
            }}>
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: '#666',
                fontStyle: 'italic',
                lineHeight: '1.5'
              }}>
                {note}
              </p>
            </div>
          )}
        </div>

        <div className="text-center" style={{ marginTop: '40px' }}>
          <Link
            to="/menu"
            className="btn btn-default"
            style={{
              background: '#7e7b5e',
              color: '#fff',
              padding: '12px 30px',
              borderRadius: '0',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              fontSize: '13px',
              fontWeight: '400',
              border: 'none'
            }}
          >
            {t('home.viewFullMenu')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DailyDish;
